<template>
  <div v-if="isVisible" class="fs-root">
    <!-- ── Caixa "Envie sua dúvida" ── -->
    <transition name="fs-pop">
      <div
        v-if="isOpen"
        class="fs-panel"
        role="dialog"
        aria-labelledby="fs-title"
        @keydown.esc="closePanel"
      >
        <header class="fs-header">
          <h3 id="fs-title" class="fs-title">Envie sua dúvida</h3>
          <button class="fs-close" aria-label="Fechar" @click="closePanel">
            <X :size="16" />
          </button>
        </header>

        <!-- Sucesso -->
        <div v-if="sent" class="fs-body fs-success">
          <CheckCircle2 :size="36" class="fs-success-icon" />
          <p class="fs-success-text">Dúvida enviada! Nossa equipe retornará em breve.</p>
          <button class="fs-btn fs-btn-primary" @click="closePanel">Fechar</button>
        </div>

        <!-- Formulário -->
        <form v-else class="fs-body" @submit.prevent="submit">
          <label class="fs-field">
            <span class="fs-label">Título</span>
            <input
              ref="titleInput"
              v-model="titulo"
              type="text"
              class="fs-input"
              maxlength="120"
              placeholder="Resuma sua dúvida"
              :disabled="loading"
            />
          </label>

          <label class="fs-field">
            <span class="fs-label">Descrição</span>
            <textarea
              v-model="descricao"
              class="fs-input fs-textarea"
              rows="4"
              maxlength="2000"
              placeholder="Descreva com detalhes o que aconteceu"
              :disabled="loading"
            />
          </label>

          <p v-if="error" class="fs-error">{{ error }}</p>

          <footer class="fs-footer">
            <button type="button" class="fs-btn fs-btn-ghost" :disabled="loading" @click="closePanel">
              Cancelar
            </button>
            <button type="submit" class="fs-btn fs-btn-primary" :disabled="!canSubmit">
              <Loader2 v-if="loading" :size="15" class="fs-spin" />
              {{ loading ? 'Enviando…' : 'Enviar' }}
            </button>
          </footer>
        </form>
      </div>
    </transition>

    <!-- ── Botão flutuante ── -->
    <button
      class="fs-fab"
      :class="{ 'fs-fab--open': isOpen }"
      :aria-label="isOpen ? 'Fechar suporte' : 'Abrir suporte'"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <X v-if="isOpen" :size="22" />
      <HelpCircle v-else :size="24" />
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { HelpCircle, X, Loader2, CheckCircle2 } from 'lucide-vue-next'
import config from '@/config'

// Telas onde o botão de suporte não aparece: login e as telas de admin
// ("Admin" / Enviar comunicação e "Painel de Controle").
const HIDDEN_ROUTES = ['login', 'admin', 'painel-de-controle']

const route = useRoute()

const isOpen     = ref(false)
const titulo     = ref('')
const descricao  = ref('')
const loading    = ref(false)
const error      = ref('')
const sent       = ref(false)
const titleInput = ref(null)

// route.name fica undefined até a primeira navegação resolver — não mostra
// nada nesse intervalo pra não piscar o botão na tela de login.
const isVisible = computed(() => !!route.name && !HIDDEN_ROUTES.includes(route.name))

const canSubmit = computed(() =>
  !loading.value && titulo.value.trim() !== '' && descricao.value.trim() !== ''
)

function resetForm() {
  titulo.value = ''
  descricao.value = ''
  error.value = ''
  sent.value = false
}

async function toggle() {
  if (isOpen.value) return closePanel()
  isOpen.value = true
  await nextTick()
  titleInput.value?.focus()
}

function closePanel() {
  if (loading.value) return
  isOpen.value = false
  resetForm()
}

// Fecha a caixa ao navegar pra outra tela (ou ao entrar numa tela oculta).
watch(() => route.fullPath, () => {
  isOpen.value = false
  resetForm()
})

async function enviarDuvida(payload) {
  const res = await fetch(`${config.backendUrl}/api/v1/suporte/duvidas`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.detail || `Erro ${res.status}`)
  }
  return res.json().catch(() => ({}))
}

async function submit() {
  if (!canSubmit.value) return
  loading.value = true
  error.value = ''
  try {
    await enviarDuvida({
      titulo: titulo.value.trim(),
      descricao: descricao.value.trim(),
      url_origem: route.fullPath,
    })
    sent.value = true
  } catch (e) {
    console.error('[FloatingSupport] enviarDuvida:', e)
    error.value = 'Não foi possível enviar sua dúvida. Tente novamente.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.fs-root {
  --fs-primary: #007bc0;
  --fs-primary-hover: #006aa6;
  --fs-border: #d5e5ec;
  --fs-text: #111828;
  --fs-muted: #6b7280;

  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 8000; /* abaixo do modal de cliente (9000) */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  font-family: 'Figtree', 'Inter', Arial, sans-serif;
}

/* ── Botão flutuante ── */
.fs-fab {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: var(--fs-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 123, 192, 0.35);
  transition: background 0.15s, transform 0.15s;
}
.fs-fab:hover { background: var(--fs-primary-hover); transform: translateY(-1px); }
.fs-fab:focus-visible { outline: 3px solid rgba(0, 123, 192, 0.35); outline-offset: 2px; }

/* ── Caixa ── */
.fs-panel {
  width: 340px;
  max-width: calc(100vw - 48px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(17, 24, 40, 0.16), 0 2px 8px rgba(17, 24, 40, 0.06);
  overflow: hidden;
  transform-origin: bottom right;
}

.fs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--fs-primary);
  color: #fff;
}
.fs-title { margin: 0; font-size: 15px; font-weight: 600; }

.fs-close {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  padding: 4px;
  border-radius: 6px;
}
.fs-close:hover { background: rgba(255, 255, 255, 0.15); }

.fs-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.fs-field { display: flex; flex-direction: column; gap: 6px; }
.fs-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fs-muted);
}

.fs-input {
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  border: 1.5px solid var(--fs-border);
  border-radius: 10px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 14px;
  color: var(--fs-text);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.fs-input:focus {
  border-color: var(--fs-primary);
  box-shadow: 0 0 0 3px rgba(0, 123, 192, 0.12);
}
.fs-input:disabled { background: #f7f9fa; }
.fs-textarea { resize: vertical; min-height: 96px; }

.fs-error {
  margin: 0;
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
}

.fs-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.fs-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s, border-color 0.15s;
}
.fs-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.fs-btn-primary {
  background: var(--fs-primary);
  border: none;
  color: #fff;
}
.fs-btn-primary:hover:not(:disabled) { background: var(--fs-primary-hover); }

.fs-btn-ghost {
  background: transparent;
  border: 1.5px solid var(--fs-border);
  color: var(--fs-text);
}
.fs-btn-ghost:hover:not(:disabled) { border-color: var(--fs-primary); color: var(--fs-primary); }

/* ── Sucesso ── */
.fs-success { align-items: center; text-align: center; padding: 24px 16px; }
.fs-success-icon { color: #16a34a; }
.fs-success-text { margin: 0; font-size: 14px; color: var(--fs-text); line-height: 1.5; }

.fs-spin { animation: fs-spin 1s linear infinite; }
@keyframes fs-spin { to { transform: rotate(360deg); } }

/* ── Transição ── */
.fs-pop-enter-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.fs-pop-leave-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.fs-pop-enter-from,
.fs-pop-leave-to { opacity: 0; transform: translateY(8px) scale(0.97); }

@media (max-width: 480px) {
  .fs-root { right: 16px; bottom: 16px; }
}
</style>
